import React, { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { Link } from '@components/atoms/link';
import { ROUTES } from '@constants';
import { useActiveProduct } from '@hooks/dashboard/use-active-product';
import { useLoadedApp } from '@hooks/use-loaded-app';
import { DashboardVariantEnum } from '@models/enums';
import type { ToastProps } from '@models/types';
import { useAppSelector } from '@store';

const ContactUsToast = ({ contactUsUrl }: { contactUsUrl: string }) => (
    <span style={{ whiteSpace: 'nowrap' }}>
        <Trans
            i18nKey='common.pleaseContactUs'
            components={{
                link1: (
                    <Link
                        to={contactUsUrl}
                        target='_blank'
                        dashed={true}
                        style={{ fontSize: 'inherit' }}
                    />
                ),
            }}
        />
    </span>
);

const CONTACT_US_ROUTES: Partial<Record<DashboardVariantEnum, string>> = {
    [DashboardVariantEnum.KYC]: ROUTES.KYC.CONTACT_US,
    [DashboardVariantEnum.KYT]: ROUTES.KYT.CONTACT_US,
    [DashboardVariantEnum.DEEPFAKE]: ROUTES.DEEPFAKE.CONTACT_US,
    [DashboardVariantEnum.CCD]: ROUTES.KYC.CONTACT_US,
};

export const useEnrichErrorToast = () => {
    // optional chaining: widget npm bundle uses a lighter store without the `dashboard`
    // slice (session lives at root there). TS sees RootState as the main app, so without
    // ?. these crash at runtime in widget context.
    const showContactUs = useAppSelector(
        (state) => state.domain?.settings?.other_settings?.show_contact_us,
    );
    const isAuthorized = useAppSelector((state) => !!state.dashboard?.session?.userId);
    const { isAdmin } = useLoadedApp();
    const { variantId } = useActiveProduct();

    const enrichToast = useCallback(
        (data: Omit<ToastProps, 'id'>): Omit<ToastProps, 'id'> => {
            if (data.variant !== 'error' || !isAuthorized) {
                return data;
            }

            let contactUsUrl: string | undefined;

            if (isAdmin) {
                contactUsUrl = ROUTES.ADMIN_PANEL.CONTACT_US;
            } else {
                if (!showContactUs) return data;
                contactUsUrl = CONTACT_US_ROUTES[variantId];
                if (!contactUsUrl) return data;
            }

            const contactUsNode = <ContactUsToast contactUsUrl={contactUsUrl} />;

            return {
                ...data,
                content: data.content ? (
                    <React.Fragment>
                        {data.content}
                        {contactUsNode}
                    </React.Fragment>
                ) : (
                    contactUsNode
                ),
            };
        },
        [showContactUs, isAuthorized, isAdmin, variantId],
    );

    return { enrichToast };
};
